import React, { useState, useEffect, useCallback } from "react";
import { Container, Form, Button, Modal, Spinner } from "react-bootstrap";
import { simplePostCall } from "../../api/ApiServices";
import ApiConfig from "../../api/ApiConfig";
import { toast, ToastContainer } from "react-toastify";
import ScaleLoader from "react-spinners/ScaleLoader";
import { formatNumber } from "../../utils/formatNumber";
import "../../Styles/yellocalfcss/Liabilities.css";

/**
 * Salaries Payable
 *
 * Tracks accrued staff salaries that are earned but not yet paid — the
 * accrual side of the double-entry. Each row represents one employee for one
 * pay period: gross, deductions, net payable, status.
 *
 * Backend dependency: ApiConfig.salariesPayableList / salariesPayablePay.
 * Expected row shape:
 *   { salary_id, employee_name, employee_id, period (e.g. "Mar 2026"),
 *     gross_salary, deductions, net_payable, paid_amount, status, due_date }
 */
const SalariesPayable = () => {
  const customer_currency = (
    localStorage.getItem("customer_currency") || ""
  ).replace(/"/g, "");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ period: "", status: "", search: "" });

  const [showPayModal, setShowPayModal] = useState(false);
  const [activeRow, setActiveRow] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payDate, setPayDate] = useState("");
  const [payNote, setPayNote] = useState("");
  const [paying, setPaying] = useState(false);

  const fetchSalaries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await simplePostCall(
        ApiConfig.salariesPayableList,
        JSON.stringify({
          customer_id: Number(localStorage.getItem("customer_id")) || 1005,
          api_key: localStorage.getItem("api_key"),
          user_id: Number(localStorage.getItem("user_id")),
          language: "en",
          period: filters.period,
          status: filters.status,
          search: filters.search,
        }),
      );
      setRows(res?.result && Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Salaries fetch failed:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchSalaries();
  }, [fetchSalaries]);

  const totals = rows.reduce(
    (acc, r) => {
      const net = parseFloat(r.net_payable) || 0;
      const paid = parseFloat(r.paid_amount) || 0;
      acc.payable += net;
      acc.paid += paid;
      acc.outstanding += net - paid;
      return acc;
    },
    { payable: 0, paid: 0, outstanding: 0 },
  );

  const openPay = (row) => {
    setActiveRow(row);
    const balance = (parseFloat(row.net_payable) || 0) - (parseFloat(row.paid_amount) || 0);
    setPayAmount(String(balance));
    setPayDate(new Date().toISOString().slice(0, 10));
    setPayNote("");
    setShowPayModal(true);
  };

  const submitPay = async () => {
    if (!activeRow || !payAmount || Number(payAmount) <= 0) {
      toast.warning("Enter a payment amount greater than zero.");
      return;
    }
    setPaying(true);
    try {
      const res = await simplePostCall(
        ApiConfig.salariesPayablePay,
        JSON.stringify({
          customer_id: Number(localStorage.getItem("customer_id")) || 1005,
          api_key: localStorage.getItem("api_key"),
          user_id: Number(localStorage.getItem("user_id")),
          salary_id: activeRow.salary_id,
          payment_amount: Number(payAmount),
          payment_date: payDate,
          payment_note: payNote,
        }),
      );
      if (res?.result) {
        toast.success("Salary payment recorded.");
        setShowPayModal(false);
        fetchSalaries();
      } else {
        toast.error(res?.message || "Payment failed.");
      }
    } catch (err) {
      toast.error("Error recording payment.");
    } finally {
      setPaying(false);
    }
  };

  return (
    <div className="liabilities-page">
      <ToastContainer />
      <Container fluid>
        <div className="special-header panel-white-all">
          <h4 className="page-title">Salaries Payable</h4>
        </div>

        <div className="lib-summary-row">
          <div className="lib-tile lib-tile-total">
            <span className="lib-tile-label">Total Payable</span>
            <span className="lib-tile-value">
              {customer_currency} {formatNumber(totals.payable)}
            </span>
          </div>
          <div className="lib-tile lib-tile-paid">
            <span className="lib-tile-label">Paid</span>
            <span className="lib-tile-value">
              {customer_currency} {formatNumber(totals.paid)}
            </span>
          </div>
          <div className="lib-tile lib-tile-outstanding">
            <span className="lib-tile-label">Outstanding</span>
            <span className="lib-tile-value">
              {customer_currency} {formatNumber(totals.outstanding)}
            </span>
          </div>
        </div>

        <div className="lib-filter-bar">
          <Form.Control
            type="text"
            placeholder="Period (e.g. Mar 2026)"
            value={filters.period}
            onChange={(e) => setFilters({ ...filters, period: e.target.value })}
          />
          <Form.Select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="">All Statuses</option>
            <option value="unpaid">Unpaid</option>
            <option value="partial">Partially Paid</option>
            <option value="paid">Paid</option>
          </Form.Select>
          <Form.Control
            type="text"
            placeholder="Search employee"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
          <Button
            onClick={fetchSalaries}
            disabled={loading}
            style={{ backgroundColor: "var(--color-primary)", border: "none" }}
          >
            {loading ? "Searching..." : "Search"}
          </Button>
        </div>

        <div className="lib-table-wrap">
          <table className="lib-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Employee</th>
                <th>Period</th>
                <th>Gross</th>
                <th>Deductions</th>
                <th>Net Payable</th>
                <th>Paid</th>
                <th>Balance</th>
                <th>Status</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="text-center py-4">
                    <ScaleLoader color="#F4BB44" />
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="10" className="text-center py-4 text-muted">
                    No accrued salaries found.
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => {
                  const net = parseFloat(r.net_payable) || 0;
                  const paid = parseFloat(r.paid_amount) || 0;
                  const bal = net - paid;
                  return (
                    <tr key={r.salary_id || idx}>
                      <td>{idx + 1}</td>
                      <td>
                        <div className="fw-semibold">{r.employee_name}</div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          {r.employee_id}
                        </div>
                      </td>
                      <td>{r.period || "—"}</td>
                      <td>{customer_currency} {formatNumber(r.gross_salary)}</td>
                      <td>{customer_currency} {formatNumber(r.deductions)}</td>
                      <td className="fw-semibold">
                        {customer_currency} {formatNumber(net)}
                      </td>
                      <td>{customer_currency} {formatNumber(paid)}</td>
                      <td className="fw-bold">
                        {customer_currency} {formatNumber(bal)}
                      </td>
                      <td>{r.status || "Unpaid"}</td>
                      <td className="text-center">
                        {bal > 0 && (
                          <Button
                            size="sm"
                            onClick={() => openPay(r)}
                            style={{ backgroundColor: "var(--color-primary)", border: "none" }}
                          >
                            Pay
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Container>

      <Modal
        show={showPayModal}
        onHide={() => !paying && setShowPayModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Record Salary Payment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {activeRow && (
            <div className="mb-3 p-2 rounded" style={{ background: "#f8f9fa" }}>
              <div><b>{activeRow.employee_name}</b></div>
              <div className="text-muted" style={{ fontSize: 13 }}>
                {activeRow.period} • Net Payable {customer_currency}{" "}
                {formatNumber(activeRow.net_payable)}
              </div>
            </div>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Payment Amount <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              min="0"
              value={payAmount}
              onChange={(e) => setPayAmount(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Payment Date</Form.Label>
            <Form.Control
              type="date"
              value={payDate}
              onChange={(e) => setPayDate(e.target.value)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Note</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={payNote}
              onChange={(e) => setPayNote(e.target.value)}
              placeholder="Bank ref, M-Pesa code, cheque #..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowPayModal(false)}
            disabled={paying}
          >
            Cancel
          </Button>
          <Button
            onClick={submitPay}
            disabled={paying}
            style={{ backgroundColor: "var(--color-primary)", border: "none" }}
          >
            {paying ? <Spinner size="sm" /> : "Record Payment"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SalariesPayable;
